import { useState, useEffect, useCallback } from "react";
import { SPECIALIST_HOSTNAME } from "env-vars";
import { fetchWithAuth } from "helpers/api-with-auth";

export interface EhrConnection {
  id: string;
  status: "active" | "expired" | "revoked" | "error" | "pending";
  connected_at: string;
  last_import_at: string | null;
  // New: EHR (healthcare organization) information
  ehr: {
    id: string;
    name: string;
    slug: string;
    logo_url: string | null;
    location_name: string | null;
    city: string | null;
    state: string | null;
  };
  /**
   * @deprecated Use ehr instead. Kept for backward compatibility.
   */
  vendor: {
    id: string;
    name: string;
    slug: string;
    logo_url: string | null;
  };
}

export interface ImportCategory {
  name: string;
  resource_type: string;
  status: "pending" | "loading" | "completed";
  count: number;
  required: boolean;
}

export interface ImportStatus {
  status: "no_connection" | "pending" | "in_progress" | "completed" | "failed" | "partial";
  progress: number;
  phase1_progress: number;
  categories: ImportCategory[];
  is_complete: boolean;
  ready_to_proceed: boolean;
  phase: "phase_1" | "phase_2" | "all_phases_complete" | "pending";
  phase_label: string;
  connection_id?: string;
  ehr_name?: string;
  /** @deprecated Use ehr_name instead */
  vendor_name?: string;
}

interface UseEhrConnectionResult {
  connections: EhrConnection[];
  connection: EhrConnection | null; // The first active connection
  isLoading: boolean;
  error: string | null;
  hasActiveConnection: boolean;
  /** True if EHR import is disabled for this organization (403 from API) */
  isDisabled: boolean;
  /**
   * Initiate an EHR connection. Pass the EHR ID (healthcare organization ID).
   * @param ehrId - The ID of the EHR (healthcare organization) to connect to
   * @param redirectUri - The redirect URI for OAuth callback
   */
  initiateConnection: (ehrId: string, redirectUri: string) => Promise<{ authorizationUrl: string; state: string } | null>;
  refreshConnection: (connectionId: string) => Promise<void>;
  revokeConnection: (connectionId: string) => Promise<void>;
}

export function useEhrConnection(): UseEhrConnectionResult {
  const [connections, setConnections] = useState<EhrConnection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDisabled, setIsDisabled] = useState(false);

  const patientId = localStorage.getItem("ciq_id");

  const fetchConnections = useCallback(async () => {
    if (!patientId) {
      setIsLoading(false);
      return;
    }

    try {
      const url = `${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}/ehr_connections`;
      const response = await fetchWithAuth(url);

      if (response.status === 403) {
        // EHR import is disabled for this organization
        setIsDisabled(true);
        setIsLoading(false);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        const connectionsList = data.ehr_connections || data;
        setConnections(Array.isArray(connectionsList) ? connectionsList : []);
      }
    } catch (err) {
      console.error("Failed to fetch EHR connections:", err);
    } finally {
      setIsLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const initiateConnection = useCallback(
    async (ehrId: string, redirectUri: string) => {
      if (!patientId) {
        setError("Not authenticated");
        return null;
      }

      try {
        const url = `${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}/ehr_connections/authorize`;
        const response = await fetchWithAuth(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ehr_id: ehrId, // Use new ehr_id parameter
            redirect_uri: redirectUri,
          }),
        });

        if (response.status === 403) {
          // EHR import is disabled for this organization
          setIsDisabled(true);
          setError("EHR import is not enabled for this organization");
          return null;
        }

        if (!response.ok) {
          throw new Error("Failed to initiate connection");
        }

        const data = await response.json();
        return {
          authorizationUrl: data.authorization_url,
          state: data.state,
        };
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
        return null;
      }
    },
    [patientId]
  );

  const refreshConnection = useCallback(
    async (connectionId: string) => {
      if (!patientId) return;

      try {
        const url = `${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}/ehr_connections/${connectionId}/refresh`;
        await fetchWithAuth(url, { method: "POST" });
      } catch (err) {
        console.error("Failed to refresh connection:", err);
      }
    },
    [patientId]
  );

  const revokeConnection = useCallback(
    async (connectionId: string) => {
      if (!patientId) return;

      try {
        const url = `${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}/ehr_connections/${connectionId}`;
        await fetchWithAuth(url, { method: "DELETE" });
        setConnections((prev) => prev.filter((c) => c.id !== connectionId));
      } catch (err) {
        console.error("Failed to revoke connection:", err);
      }
    },
    [patientId]
  );

  const activeConnection = Array.isArray(connections) 
    ? connections.find((c) => c.status === "active") || null 
    : null;
  const hasActiveConnection = activeConnection !== null;

  return {
    connections,
    connection: activeConnection,
    isLoading,
    error,
    hasActiveConnection,
    isDisabled,
    initiateConnection,
    refreshConnection,
    revokeConnection,
  };
}

export function useEhrImportStatus(): {
  importStatus: ImportStatus | null;
  isLoading: boolean;
  isDisabled: boolean;
  refetch: () => void;
} {
  const [importStatus, setImportStatus] = useState<ImportStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);

  const patientId = localStorage.getItem("ciq_id");

  const fetchStatus = useCallback(async () => {
    if (!patientId) {
      setIsLoading(false);
      return;
    }

    try {
      const url = `${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}/ehr_connections/import_status`;
      const response = await fetchWithAuth(url);

      if (response.status === 403) {
        // EHR import is disabled for this organization
        setIsDisabled(true);
        setIsLoading(false);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setImportStatus(data);
      }
    } catch (err) {
      console.error("Failed to fetch import status:", err);
    } finally {
      setIsLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return {
    importStatus,
    isLoading,
    isDisabled,
    refetch: fetchStatus,
  };
}
